import { ReactNode, useEffect, useRef } from 'react';
import cx from 'classnames';

// Design System
import { Message } from 'checkout-forms/components/message/Message';

// Styles
import styles from './day-picker-selected-day.scss';

type MessageRole = 'alert' | 'information';
type MessageType = 'information' | 'error' | 'success' | 'warning';
type DayPickerSelectedDayMessageType = MessageType | 'info';

interface DayPickerSelectedDayProps {
  additionalInformationOnSelect?: ReactNode;
  focusOnSelectedDayMessage?: boolean;
  selectedDay?: string;
  selectedDayMessageBody?: ReactNode;
  selectedDayMessageClassName?: string;
  selectedDayMessageRole?: MessageRole;
  selectedDayMessageTitle?: string;
  selectedDayMessageType?: DayPickerSelectedDayMessageType;
}

const DayPickerSelectedDay = ({
  additionalInformationOnSelect,
  focusOnSelectedDayMessage = true,
  selectedDay,
  selectedDayMessageBody,
  selectedDayMessageClassName,
  selectedDayMessageRole = 'alert',
  selectedDayMessageTitle,
  selectedDayMessageType = 'information',
}: DayPickerSelectedDayProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!focusOnSelectedDayMessage || !containerRef.current) {
      return;
    }

    const message = containerRef.current.querySelector('div[role="alert"]') as HTMLElement | null;

    if (message?.focus) {
      message.focus();
    }
  }, [focusOnSelectedDayMessage, selectedDay]);

  const messageType: MessageType = selectedDayMessageType === 'info' ? 'information' : selectedDayMessageType;

  return (
    <div ref={containerRef} data-jlds={true}>
      {selectedDayMessageTitle && (
        <Message
          body={selectedDayMessageBody}
          className={cx(styles.selectedDayMessage, selectedDayMessageClassName)}
          role={selectedDayMessageRole}
          title={selectedDayMessageTitle}
          type={messageType}
        />
      )}

      {additionalInformationOnSelect && (
        <p data-testid="day-picker-additional-information">{additionalInformationOnSelect}</p>
      )}
    </div>
  );
};

export default DayPickerSelectedDay;
